/**
 * DYMO Label Printing Service for Kalotte coil labels.
 *
 * Requires DYMO Connect Software installed on the operator PC.
 *
 * Pattern ported from LiEBatManuAssTool/frontend/src/services/dymo.service.ts.
 * Key insight: dymo.label.framework.init(callback) starts async service
 * discovery. getPrinters() returns empty if called before discovery completes.
 * Use initDymoAsync() which waits for the init callback before resolving.
 */

// DYMO framework types. The script is injected by loadDymoScript() the first
// time a label is printed, not on every page.
declare const dymo: {
  label: {
    framework: {
      init: (callback?: () => void) => void;
      checkEnvironment: () => { isBrowserSupported: boolean; isFrameworkInstalled: boolean };
      getPrinters: () => Array<{
        name: string;
        modelName: string;
        isConnected: boolean;
        isLocal: boolean;
      }>;
      getPrintersAsync: () => PromiseLike<
        Array<{ name: string; modelName: string; isConnected: boolean; isLocal: boolean }>
      >;
      printLabel: (
        printerName: string,
        printParamsXml: string,
        labelXml: string,
        labelSetXml: string
      ) => void;
    };
  };
};

export interface PrintResult {
  success: boolean;
  error?: string;
}

export interface PrinterInfo {
  name: string;
  model: string;
  isConnected: boolean;
}

/**
 * Check if DYMO framework is available (script loaded).
 */
export function isDymoAvailable(): boolean {
  return typeof dymo !== 'undefined' && dymo?.label?.framework !== undefined;
}

let dymoScriptPromise: Promise<boolean> | null = null;

/** Load the DYMO framework once. Does not contact the local printer service. */
export function loadDymoScript(): Promise<boolean> {
  if (isDymoAvailable()) return Promise.resolve(true);
  if (dymoScriptPromise) return dymoScriptPromise;
  dymoScriptPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL || ''}/dymo.connect.framework.js`;
    script.async = true;
    script.onload = () => resolve(isDymoAvailable());
    script.onerror = () => {
      dymoScriptPromise = null;
      resolve(false);
    };
    document.head.appendChild(script);
  });
  return dymoScriptPromise;
}

/**
 * Initialize DYMO framework and wait for service discovery to complete.
 * init(callback) fires the callback when the DYMO web service is found.
 */
export function initDymoAsync(): Promise<boolean> {
  return loadDymoScript().then(
    (loaded) =>
      new Promise((resolve) => {
        if (!loaded || !isDymoAvailable()) {
          resolve(false);
          return;
        }
        try {
          let done = false;
          const timer = setTimeout(() => {
            if (!done) {
              done = true;
              resolve(true);
            }
          }, 10000);
          dymo.label.framework.init(() => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            resolve(true);
          });
        } catch (err) {
          console.error('Failed to initialize DYMO:', err);
          resolve(false);
        }
      })
  );
}

/**
 * Check DYMO environment: browser support + DYMO Connect Software install.
 */
export function checkDymoEnvironment(): {
  browserSupported: boolean;
  frameworkInstalled: boolean;
} {
  if (!isDymoAvailable()) {
    return { browserSupported: false, frameworkInstalled: false };
  }
  try {
    const env = dymo.label.framework.checkEnvironment();
    return {
      browserSupported: env.isBrowserSupported,
      frameworkInstalled: env.isFrameworkInstalled,
    };
  } catch (err) {
    console.error('Failed to check DYMO environment:', err);
    return { browserSupported: false, frameworkInstalled: false };
  }
}

/**
 * Get available DYMO printers (sync — may return empty before init).
 */
export function getDymoPrinters(): PrinterInfo[] {
  if (!isDymoAvailable()) return [];
  try {
    const printers = dymo.label.framework.getPrinters();
    return (printers || []).map((p) => ({
      name: p.name,
      model: p.modelName,
      isConnected: p.isConnected,
    }));
  } catch (err) {
    console.error('Failed to get printers:', err);
    return [];
  }
}

/**
 * Get available DYMO printers (async — preferred after init callback).
 */
export async function getDymoPrintersAsync(): Promise<PrinterInfo[]> {
  if (!isDymoAvailable()) return [];
  try {
    if (typeof dymo.label.framework.getPrintersAsync === 'function') {
      const printers = await dymo.label.framework.getPrintersAsync();
      return (printers || []).map((p) => ({
        name: p.name,
        model: p.modelName,
        isConnected: p.isConnected,
      }));
    }
    return getDymoPrinters();
  } catch (err) {
    console.error('Failed to get printers async:', err);
    return [];
  }
}

/**
 * Get the first connected DYMO printer (or first listed if none reports connected).
 */
export function getDefaultPrinter(): PrinterInfo | null {
  const printers = getDymoPrinters();
  return printers.find((p) => p.isConnected) || printers[0] || null;
}

function resolvePrinter(printerName?: string): string | PrintResult {
  if (!isDymoAvailable()) {
    return { success: false, error: 'DYMO Connect Software not installed or not running' };
  }
  const printer = printerName || getDefaultPrinter()?.name;
  if (!printer) {
    return { success: false, error: 'No DYMO printer found' };
  }
  return printer;
}

/**
 * Generate the DesktopLabel XML for a Kalotte coil label.
 * Label size: 89 x 36 mm (DYMO 30252 Address Label, landscape).
 *
 * Layout on a DYMO 30252 address label, 89 × 28 mm. The printable area is
 *   about 3.04 × 0.99 in. The barcode must stay inside that area or DYMO
 *   draws nothing.
 *   • One line: charge number and coil number
 *   • Code 128 of "{chargeNumber}-{coilNumber}", the rest of the height.
 *     That string is Coil.barcode, which order entry scans.
 *
 * Mirrors the existing backend BarcodeService.generate_single_label_pdf
 * content so an existing scanned barcode and a freshly-printed one are
 * the same string.
 *
 * NOTE: the user has explicitly deferred final label content/layout —
 * this is V1 and intentionally simple. Adjust the ObjectLayout x/y/Size
 * values once a real DYMO is available for visual iteration.
 */
function xmlText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function generateCoilLabelXml(chargeNumber: string, coilNumber: string): string {
  const charge = xmlText(chargeNumber);
  const coil = xmlText(coilNumber);
  const barcodeData = xmlText(`${chargeNumber}-${coilNumber}`);
  return `<?xml version="1.0" encoding="utf-8"?>
<DesktopLabel Version="1">
  <DYMOLabel Version="4">
    <Description>Kalotte Coil Label</Description>
    <Orientation>Landscape</Orientation>
    <LabelName>Address</LabelName>
    <InitialLength>0</InitialLength>
    <BorderStyle>SolidLine</BorderStyle>
    <DYMORect>
      <DYMOPoint><X>0.23</X><Y>0.05</Y></DYMOPoint>
      <Size><Width>3.04</Width><Height>0.99</Height></Size>
    </DYMORect>
    <BorderColor><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></BorderColor>
    <BorderThickness>1</BorderThickness>
    <Show_Border>False</Show_Border>
    <DynamicLayoutManager>
      <RotationBehavior>ClearObjects</RotationBehavior>
      <LabelObjects>
        <TextObject>
          <Name>ChargeText</Name>
          <Brushes>
            <BackgroundBrush><SolidColorBrush><Color A="0" R="0" G="0" B="0" /></SolidColorBrush></BackgroundBrush>
            <BorderBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></BorderBrush>
            <StrokeBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></StrokeBrush>
            <FillBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></FillBrush>
          </Brushes>
          <Rotation>Rotation0</Rotation>
          <OutlineThickness>1</OutlineThickness>
          <IsOutlined>False</IsOutlined>
          <BorderStyle>SolidLine</BorderStyle>
          <Margin><DYMOThickness Left="0" Top="0" Right="0" Bottom="0" /></Margin>
          <HorizontalAlignment>Left</HorizontalAlignment>
          <VerticalAlignment>Middle</VerticalAlignment>
          <FitMode>AlwaysFit</FitMode>
          <IsVertical>False</IsVertical>
          <FormattedText>
            <FitMode>AlwaysFit</FitMode>
            <HorizontalAlignment>Left</HorizontalAlignment>
            <VerticalAlignment>Middle</VerticalAlignment>
            <IsVertical>False</IsVertical>
            <LineTextSpan>
              <TextSpan>
                <Text>Charge ${charge}  Coil ${coil}</Text>
                <FontInfo>
                  <FontName>Arial</FontName>
                  <FontSize>12</FontSize>
                  <IsBold>True</IsBold>
                  <IsItalic>False</IsItalic>
                  <IsUnderline>False</IsUnderline>
                  <FontBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></FontBrush>
                </FontInfo>
              </TextSpan>
            </LineTextSpan>
          </FormattedText>
          <ObjectLayout>
            <DYMOPoint><X>0.25</X><Y>0.06</Y></DYMOPoint>
            <Size><Width>2.95</Width><Height>0.18</Height></Size>
          </ObjectLayout>
        </TextObject>
        <BarcodeObject>
          <Name>CoilBarcode</Name>
          <Brushes>
            <BackgroundBrush><SolidColorBrush><Color A="1" R="1" G="1" B="1" /></SolidColorBrush></BackgroundBrush>
            <BorderBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></BorderBrush>
            <StrokeBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></StrokeBrush>
            <FillBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></FillBrush>
          </Brushes>
          <Rotation>Rotation0</Rotation>
          <OutlineThickness>1</OutlineThickness>
          <IsOutlined>False</IsOutlined>
          <BorderStyle>SolidLine</BorderStyle>
          <Margin><DYMOThickness Left="0" Top="0" Right="0" Bottom="0" /></Margin>
          <BarcodeFormat>Code128Auto</BarcodeFormat>
          <Data>
            <DataString>${barcodeData}</DataString>
          </Data>
          <HorizontalAlignment>Center</HorizontalAlignment>
          <VerticalAlignment>Middle</VerticalAlignment>
          <Size>AutoFit</Size>
          <TextPosition>Bottom</TextPosition>
          <FontInfo>
            <FontName>Arial</FontName>
            <FontSize>9</FontSize>
            <IsBold>False</IsBold>
            <IsItalic>False</IsItalic>
            <IsUnderline>False</IsUnderline>
            <FontBrush><SolidColorBrush><Color A="1" R="0" G="0" B="0" /></SolidColorBrush></FontBrush>
          </FontInfo>
          <ObjectLayout>
            <DYMOPoint><X>0.28</X><Y>0.26</Y></DYMOPoint>
            <Size><Width>2.90</Width><Height>0.74</Height></Size>
          </ObjectLayout>
        </BarcodeObject>
      </LabelObjects>
    </DynamicLayoutManager>
  </DYMOLabel>
  <LabelApplication>Blank</LabelApplication>
  <DataTable>
    <Columns></Columns>
    <Rows></Rows>
  </DataTable>
</DesktopLabel>`;
}

/**
 * Print a single Kalotte coil label.
 */
export function printCoilLabel(
  chargeNumber: string,
  coilNumber: string,
  printerName?: string
): PrintResult {
  const printer = resolvePrinter(printerName);
  if (typeof printer !== 'string') return printer;

  try {
    const labelXml = generateCoilLabelXml(chargeNumber, coilNumber);
    // Text quality prints the charge line and drops the barcode.
    dymo.label.framework.printLabel(
      printer,
      '<LabelWriterPrintParams><PrintQuality>BarcodeAndGraphics</PrintQuality></LabelWriterPrintParams>',
      labelXml,
      ''
    );
    return { success: true };
  } catch (err) {
    console.error('Print failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Print failed',
    };
  }
}
